<template>
  <fn-form
    v-model="data.fields"
    @form-loaded="onFormLoaded"
    @submit="onSubmit"
  >
    <template #default>
      <fn-form-errors :errors="errors" />

      <fn-form-group>
        <fn-form-section>
          <fn-form-field-input
            class="form-input-email"
            :class="{ 'form-input-email--full-width': true }"
            name="email"
            type="email"
            :rules="{
              email: true,
            }"
            :label="$t('forms.login.fields.email.label')"
            full-width
          />
        </fn-form-section>
      </fn-form-group>

      <fn-form-group>
        <fn-form-section>
          <fn-form-field-password
            class="form-input-password"
            name="password"
            :rules="{
              required: true,
              min: 8,
            }"
            hide-required-asterix
            :label="$t('forms.login.fields.password.label')"
            :password-strength-visible="false"
          />
        </fn-form-section>
      </fn-form-group>

      <fn-form-group>
        <fn-form-section>
          <fn-button
            id="forms-login-button-submit"
            full-width
            submit
            :secondary="!friendsLight"
            :quaternary-alt="friendsLight"
            :orientation-green="salon"
          >
            {{ $t('forms.login.buttons.submit') }}
          </fn-button>
        </fn-form-section>
      </fn-form-group>

      <fn-form-group v-if="registerButton">
        <fn-form-section>
          <fn-button
            id="forms-login-button-register"
            full-width
            secondary
            :disabled="userStore.softLogin"
            @click="onRegisterBtnClick"
          >
            {{ $t('forms.login.buttons.registerNew') }}
          </fn-button>
        </fn-form-section>
      </fn-form-group>

      <fn-form-group>
        <fn-form-section>
          <div :class="['password-forgotten', { 'password-forgotten--friends-light': friendsLight }]">
            <fn-button
              v-if="!friendsLight"
              id="formLogin-forgotPassword"
              plain
              full-width
              @click="onForgotPasswordLinkClick"
            >
              {{ $t('forms.login.buttons.forgotPassword') }}
            </fn-button>
            <fn-link
              v-else
              id="formLogin-forgotPassword"
              no-arrow
              underline
              @click="onForgotPasswordLinkClick"
            >
              {{ $t('forms.login.buttons.forgotPassword') }}
            </fn-link>
          </div>
        </fn-form-section>
      </fn-form-group>

      <fn-form-group v-if="mainStore.getLoginRecaptchaEnabled">
        <fn-form-section>
          <fn-recaptcha
            ref="recaptcha"
            small-print
            no-margin
            @verify="handleSubmit"
          />
        </fn-form-section>
      </fn-form-group>
    </template>
  </fn-form>
</template>

<script setup lang="ts">
import FnRecaptcha from '@/components/atoms/recaptcha/Recaptcha.vue';
import { actions } from '@/utils/recaptcha';
import type { FormContext, GenericObject } from 'vee-validate';

const emit = defineEmits(['submit', 'forgotPassword', 'registrationEnabled']);

const props = withDefaults(
  defineProps<{
    errors?: Record<string, any>;
    email: string;
    preserveValue: boolean;
    registerButton: boolean;
    enableRegistration: boolean;
    friendsLight: boolean;
    salon: boolean;
    fullWidthInputs: boolean;
  }>(),
  {
    errors: () => ({}),
  },
);

const nuxtApp = useNuxtApp();
const $router = useRouter();
const mainStore = useMainStore();
const userStore = useUserStore();

const data = reactive({
  fields: {
    email: '',
    password: '',
    recaptchaResponse: '',
  },
});

const recaptcha = ref<any>();

const onFormLoaded = ({ form }: { form: FormContext<GenericObject, GenericObject> }) => {
  if (props.email) {
    form.setFieldValue('email', props.email);
  } else if (mainStore.preservedEmail) {
    form.setFieldValue('email', mainStore.preservedEmail);
  }
};

onBeforeUnmount(() => {
  mainStore.preservedEmail = data.fields.email;
});

const onRegisterBtnClick = async () => {
  if (props.enableRegistration) {
    emit('registrationEnabled');
    return;
  }

  return navigateTo({
    path: nuxtApp.$i18n.t('url.registration'),
    query: { redirectUrl: unref($router.currentRoute).path },
  });
};

const onForgotPasswordLinkClick = () => {
  userStore.passwordUpdateRequired = false;
  emit('forgotPassword', data.fields.email);
};

const handleSubmit = (recaptchaFields: { recaptchaResponse: string } | undefined = undefined) => {
  // Add recaptcha to login if necessary
  const values = mainStore.getLoginRecaptchaEnabled
    ? { ...data.fields, recaptchaResponse: recaptchaFields?.recaptchaResponse }
    : { ...data.fields };

  // Preserve mail in case of failed logins
  mainStore.preservedEmail = values.email;

  emit('submit', values);
};

const onSubmit = () => {
  if (mainStore.getLoginRecaptchaEnabled) {
    recaptcha?.value?.execute(actions.login);
  } else {
    handleSubmit();
  }
};
</script>

<style lang="scss" scoped>
:deep(.form-input-email) {
  .form-input-email--full-width {
    width: 100%;
  }
}

.password-forgotten {
  .button {
    justify-content: center;
  }
}

.password-forgotten--friends-light {
  text-align: center;
}
</style>
